import React, { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { AssetAllocation as AssetAllocationType } from '../types';
import { formatINR } from '../utils/currency';

const AssetAllocation: React.FC = () => {
  const [allocations, setAllocations] = useState<AssetAllocationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAllocations();
  }, []);

  const fetchAllocations = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPortfolioAllocation();
      setAllocations(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load asset allocation');
      console.error('Error fetching asset allocation:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Debt': 'bg-blue-500',
      'Equity': 'bg-green-500',
      'Metals': 'bg-yellow-500',
    };
    return colors[category] || 'bg-gray-500';
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      'Debt': '🏦',
      'Equity': '📈',
      'Metals': '🥇',
    };
    return icons[category] || '💼';
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-6">
        <h3 className="text-lg font-semibold mb-4">Asset Allocation</h3>
        <div className="space-y-4 animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-white/10 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
        <h3 className="text-lg font-semibold mb-2">Asset Allocation</h3>
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (allocations.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-6">
        <h3 className="text-lg font-semibold mb-4">Asset Allocation</h3>
        <p className="text-slate-400 text-center py-8">No assets in portfolio yet</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-6">
      <h3 className="text-lg font-semibold mb-6">Asset Allocation</h3>
      
      {/* Visual bar chart */}
      <div className="mb-6">
        <div className="flex h-8 rounded-lg overflow-hidden">
          {allocations.map((allocation, index) => {
            const percentage = parseFloat(allocation.percentage);
            return (
              <div
                key={index}
                className={`${getCategoryColor(allocation.category)} transition-all hover:opacity-80`}
                style={{ width: `${percentage}%` }}
                title={`${allocation.category}: ${percentage.toFixed(1)}%`}
              />
            );
          })}
        </div>
      </div>

      {/* Allocation breakdown */}
      <div className="space-y-3">
        {allocations.map((allocation, index) => {
          const amount = parseFloat(allocation.amount);
          const percentage = parseFloat(allocation.percentage);
          
          return (
            <div
              key={index}
              className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{getCategoryIcon(allocation.category)}</span>
                <div>
                  <p className="font-medium text-white">{allocation.category}</p>
                  <p className="text-sm text-slate-400">{allocation.count} asset{allocation.count !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-white">{formatINR(amount)}</p>
                <p className="text-sm text-slate-400">{percentage.toFixed(1)}%</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssetAllocation;